import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, User, Calendar, ShieldCheck, School, ChevronDown, Search, Bell, BookOpen, Clock } from 'lucide-react';
import api from '../../connector/api';

export default function UniformInfractionModal({ isOpen, onClose, onSave, infractionData = null }) {
  // Metadata lists
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [activeTerm, setActiveTerm] = useState(null);

  // Form Field States
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedTermId, setSelectedTermId] = useState(''); 
  
  // Violations selection state
  const [selectedViolations, setSelectedViolations] = useState([]);
  
  // DateTime state
  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };
  const [date, setDate] = useState(getCurrentDateTime());

  // Intervention & Notes
  const [selectedIntervention, setSelectedIntervention] = useState('verbal');
  const [notes, setNotes] = useState('');
  const [parentAlerted, setParentAlerted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custom Dropdown UI Toggles
  const [isClassOpen, setIsClassOpen] = useState(false);
  const [isStudentOpen, setIsStudentOpen] = useState(false);
  const [isSubjectOpen, setIsSubjectOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  // Click Outside References
  const classDropdownRef = useRef(null);
  const studentDropdownRef = useRef(null);
  const subjectDropdownRef = useRef(null);

  const violationOptions = [
    "No ID Card",
    "No Tie",
    "No PE Uniform",
    "Wrong Shoes",
    "Wrong Hair Color",
    "No School Logo",
    "Wrong Shirt Sleeves",
    "Wrong ID string"
  ];

  const interventionOptions = [
    { key: 'verbal', label: 'Verbal Warning' },
    { key: 'telegram', label: 'Telegram Alert' },
    { key: 'call', label: 'Phone Call' },
    { key: 'detention', label: 'Detention' }
  ];

  const getId = (val) => {
    if (!val) return '';
    return typeof val === 'object' ? String(val.id) : String(val);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (classDropdownRef.current && !classDropdownRef.current.contains(event.target)) {
        setIsClassOpen(false);
      }
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target)) {
        setIsStudentOpen(false);
      }
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(event.target)) {
        setIsSubjectOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchMetadata = async () => {
        try {
          const [classRes, termRes] = await Promise.all([
            api.get('/academic/classroom/'),
            api.get('/year-terms/')
          ]);
          
          if (classRes.data) setClassrooms(classRes.data.results || classRes.data);
          
          if (termRes.data) {
            const terms = termRes.data.results || termRes.data;
            const current = terms.find(t => t.is_current) || terms[0];
            if (current) {
              setActiveTerm(current);
              if (!infractionData) {
                setSelectedTermId(current.id);
              }
            }
          }
        } catch (err) {
          console.error("Failed to load metadata options:", err);
        }
      };
      fetchMetadata();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (infractionData) {
      const studentId = getId(infractionData.student);
      const subjectId = getId(infractionData.subject);
      const termId = getId(infractionData.school_year_term);

      setSelectedStudentId(studentId);
      setSelectedSubjectId(subjectId);
      setSelectedTermId(termId);

      if (infractionData.specific_item) {
        setSelectedViolations([infractionData.specific_item]);
      } else {
        setSelectedViolations([]);
      }

      if (infractionData.date_of_infraction) {
        const d = new Date(infractionData.date_of_infraction);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        setDate(d.toISOString().slice(0, 16));
      }

      setSelectedIntervention(infractionData.intervention_applied || 'verbal');

      const currentNote = infractionData.detailed_note || '';
      if (currentNote.includes('[PARENT ALERTED ADMINISTRATION]')) {
        setParentAlerted(true);
        setNotes(currentNote.replace('[PARENT ALERTED ADMINISTRATION] ', ''));
      } else {
        setParentAlerted(false);
        setNotes(currentNote);
      }
    } else {
      setSelectedClassroom('');
      setSelectedStudentId('');
      setSelectedSubjectId('');
      setSelectedViolations([]);
      setDate(getCurrentDateTime());
      setSelectedIntervention('verbal');
      setNotes('');
      setParentAlerted(false);
    }

    setIsClassOpen(false);
    setIsStudentOpen(false);
    setIsSubjectOpen(false);
    setStudentSearch('');
  }, [infractionData, isOpen]);

  useEffect(() => {
    if (infractionData && classrooms.length > 0) {
      const studentId = getId(infractionData.student);
      const subjectId = getId(infractionData.subject);

      const foundClass = classrooms.find(cls => 
        cls.students?.some(s => getId(s) === studentId) ||
        cls.subjects?.some(sub => getId(sub) === subjectId)
      );

      if (foundClass) {
        setSelectedClassroom(String(foundClass.id));
      }
    }
  }, [classrooms, infractionData]);

  useEffect(() => {
    if (!selectedClassroom) {
      setStudents([]);
      setSubjects([]);
      return;
    }

    const currentClass = classrooms.find(
      (cls) => String(cls.id) === String(selectedClassroom)
    );

    if (currentClass) {
      const rawStudents = currentClass.students || currentClass.enrollments || [];
      const normalizedStudents = rawStudents.map((item) => {
        if (item.student && typeof item.student === 'object') {
          return item.student;
        }
        return item;
      });

      setStudents(normalizedStudents);
      setSubjects(currentClass.subjects || []);
    } else {
      setStudents([]);
      setSubjects([]);
    }
    setStudentSearch('');
  }, [selectedClassroom, classrooms]);

  const currentSelectedClassroomObj = classrooms.find(c => String(c.id) === String(selectedClassroom));
  const currentSelectedStudentObj = students.find(s => String(s.id) === String(selectedStudentId));
  const currentSelectedSubjectObj = subjects.find(s => String(s.id) === String(selectedSubjectId));

  const filteredStudents = students.filter(stu => {
    const searchString = `${stu.first_name || ''} ${stu.last_name || ''}`.toLowerCase();
    return searchString.includes(studentSearch.toLowerCase());
  });

  const handleViolationCheckbox = (item) => {
    if (infractionData) {
      setSelectedViolations([item]);
    } else {
      if (selectedViolations.includes(item)) {
        setSelectedViolations(selectedViolations.filter(v => v !== item));
      } else {
        setSelectedViolations([...selectedViolations, item]);
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedStudentId || selectedViolations.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const formattedNote = parentAlerted 
        ? `[PARENT ALERTED ADMINISTRATION] ${notes}`.trim()
        : notes.trim();

      const basePayload = {
        student: parseInt(selectedStudentId, 10),
        subject: selectedSubjectId ? parseInt(selectedSubjectId, 10) : null,
        school_year_term: selectedTermId ? parseInt(selectedTermId, 10) : null,
        date_of_infraction: new Date(date).toISOString(),
        intervention_applied: selectedIntervention,
        detailed_note: formattedNote
      };

      if (infractionData) {
        const payload = {
          ...basePayload,
          specific_item: selectedViolations[0] || 'Unspecified Item'
        };
        await onSave(payload, infractionData.id);
      } else {
        if (selectedViolations.length > 1) {
          await Promise.all(
            selectedViolations.map(item =>
              onSave({ ...basePayload, specific_item: item }, null)
            )
          );
        } else {
          await onSave({ ...basePayload, specific_item: selectedViolations[0] }, null);
        }
      }

      onClose();
    } catch (error) {
      console.error("Modal submission error:", error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: "spring", duration: 0.4, bounce: 0.15 }}
            className="relative bg-white w-full max-w-4xl rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col z-10 max-h-[90vh]"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className={`p-2 rounded-lg ${infractionData ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900">
                    {infractionData ? 'Modify Infraction Record' : 'File Dress-Code Infraction'}
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {infractionData ? `Updating Entry ID: #${infractionData.id}` : 'Log standardized compliance deviation event.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="px-3 py-1 bg-slate-100 border border-slate-200/80 rounded-lg flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-700">
                    {activeTerm ? `${activeTerm.term?.name || activeTerm.term_name || 'Term'} (${activeTerm.school_year?.name || activeTerm.school_year_name || ''})` : "Loading term..."}
                  </span>
                  {activeTerm?.is_current && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.2 rounded-full">
                      Active
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} id="infraction-form" className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {!infractionData && (
                  <div className="space-y-1.5 relative" ref={classDropdownRef}>
                    <label className="text-[10px] font-extrabold text-slate-400 uppercase block">Classroom Lookup</label>
                    <button
                      type="button"
                      onClick={() => setIsClassOpen(!isClassOpen)}
                      className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 rounded-lg pl-9 pr-10 py-2 text-xs text-left text-slate-800 flex items-center justify-between transition cursor-pointer h-9"
                    >
                      <School className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      <span className="truncate">
                        {currentSelectedClassroomObj 
                          ? (currentSelectedClassroomObj.name || currentSelectedClassroomObj.section_name || currentSelectedClassroomObj.section?.name || `Classroom #${currentSelectedClassroomObj.id}`)
                          : "Select section..."
                        }
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isClassOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isClassOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="absolute left-0 right-0 mt-1 max-h-48 bg-white border border-slate-200 rounded-lg shadow-lg overflow-y-auto z-30 py-1"
                        >
                          {classrooms.length === 0 ? (
                            <div className="px-4 py-2 text-xs text-slate-400 italic">No classrooms available</div>
                          ) : (
                            classrooms.map((cls) => (
                              <button
                                key={cls.id}
                                type="button"
                                onClick={() => {
                                  setSelectedClassroom(cls.id);
                                  setSelectedStudentId('');
                                  setSelectedSubjectId('');
                                  setIsClassOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 text-slate-700 block truncate cursor-pointer"
                              >
                                {cls.name || cls.section_name || cls.section?.name || `Classroom #${cls.id}`}
                              </button>
                            ))
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                <div className="space-y-1.5 relative" ref={studentDropdownRef}>
                  <label className="text-[10px] font-extrabold text-slate-400 uppercase block">Student Profile</label>
                  <button
                    type="button"
                    disabled={!!infractionData || !selectedClassroom}
                    onClick={() => setIsStudentOpen(!isStudentOpen)}
                    className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 rounded-lg pl-9 pr-10 py-2 text-xs text-left text-slate-800 flex items-center justify-between transition disabled:opacity-60 disabled:cursor-not-allowed h-9 cursor-pointer"
                  >
                    <User className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <span className="truncate">
                      {infractionData ? (
                        infractionData.student_name || `Student #${getId(infractionData.student)}`
                      ) : currentSelectedStudentObj ? (
                        `${currentSelectedStudentObj.last_name || ''}, ${currentSelectedStudentObj.first_name || ''}`
                      ) : !selectedClassroom ? (
                        "Choose classroom first..."
                      ) : (
                        "Choose student..."
                      )}
                    </span>
                    {!infractionData && selectedClassroom && (
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isStudentOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  <AnimatePresence>
                    {isStudentOpen && !infractionData && selectedClassroom && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 flex flex-col max-h-56 overflow-hidden"
                      >
                        <div className="p-2 border-b border-slate-100 relative bg-slate-50/50">
                          <Search className="absolute left-4.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Search student..."
                            value={studentSearch}
                            onChange={(e) => setStudentSearch(e.target.value)}
                            className="w-full bg-white border border-slate-200 focus:border-slate-300 rounded-md pl-7 pr-3 py-1.5 text-xs outline-none"
                          />
                        </div>
                        <div className="overflow-y-auto py-1 max-h-40 flex-1">
                          {filteredStudents.length === 0 ? (
                            <div className="px-4 py-2 text-xs text-slate-400 italic">No matching students</div>
                          ) : (
                            filteredStudents.map((stu) => (
                              <button
                                key={stu.id}
                                type="button"
                                onClick={() => {
                                  setSelectedStudentId(stu.id);
                                  setIsStudentOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 text-slate-700 block truncate cursor-pointer"
                              >
                                {stu.last_name ? `${stu.last_name}, ${stu.first_name}` : stu.first_name}
                              </button>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-1.5 relative" ref={subjectDropdownRef}>
                  <label className="text-[10px] font-extrabold text-slate-400 uppercase block">Subject / Period (Optional)</label>
                  <button
                    type="button"
                    disabled={!selectedClassroom}
                    onClick={() => setIsSubjectOpen(!isSubjectOpen)}
                    className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 rounded-lg pl-9 pr-10 py-2 text-xs text-left text-slate-800 flex items-center justify-between transition disabled:opacity-60 disabled:cursor-not-allowed h-9 cursor-pointer"
                  >
                    <BookOpen className="absolute left-3 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <span className="truncate text-slate-800">
                      {!selectedClassroom ? (
                        "Choose classroom first..."
                      ) : currentSelectedSubjectObj ? (
                        currentSelectedSubjectObj.display_name || currentSelectedSubjectObj.custom_name || currentSelectedSubjectObj.subject?.name || `Subject #${currentSelectedSubjectObj.id}`
                      ) : (
                        "Select assigned subject..."
                      )}
                    </span>
                    {selectedClassroom && (
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isSubjectOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  <AnimatePresence>
                    {isSubjectOpen && selectedClassroom && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute left-0 right-0 mt-1 max-h-48 bg-white border border-slate-200 rounded-lg shadow-lg overflow-y-auto z-30 py-1"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSubjectId('');
                            setIsSubjectOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-xs italic text-slate-400 hover:bg-slate-50 block cursor-pointer"
                        >
                          None / Homeroom General
                        </button>
                        {subjects.length === 0 ? (
                          <div className="px-4 py-2 text-xs text-slate-400 italic">No assigned subjects</div>
                        ) : (
                          subjects.map((subj) => (
                            <button
                              key={subj.id}
                              type="button"
                              onClick={() => {
                                setSelectedSubjectId(subj.id);
                                setIsSubjectOpen(false);
                              }}
                              className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 text-slate-700 block truncate cursor-pointer"
                            >
                              {subj.display_name || subj.custom_name || subj.subject?.name || `Subject #${subj.id}`}
                            </button>
                          ))
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 block">
                    {infractionData ? 'Infraction Type' : 'Select Infraction Types (Batch Log)'}
                  </label>
                  <div className="flex flex-wrap gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                    {violationOptions.map((option) => (
                      <label 
                        key={option} 
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg shadow-2xs hover:border-slate-300 cursor-pointer select-none transition"
                      >
                        <input
                          type={infractionData ? "radio" : "checkbox"}
                          name="violation_item"
                          checked={selectedViolations.includes(option)}
                          onChange={() => handleViolationCheckbox(option)}
                          className="accent-slate-800 rounded border-slate-300 w-3.5 h-3.5"
                        />
                        <span className="text-xs font-medium text-slate-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 block">Date & Time Observed</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="datetime-local"
                      required
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 rounded-lg pl-9 pr-4 h-9 text-xs text-slate-800 outline-none transition cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700 block">Intervention Action</label>
                    <div className="grid grid-cols-2 gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                      {interventionOptions.map((opt) => (
                        <label 
                          key={opt.key} 
                          className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg shadow-2xs hover:border-slate-300 cursor-pointer select-none transition"
                        >
                          <input
                            type="radio"
                            name="intervention"
                            value={opt.key}
                            checked={selectedIntervention === opt.key}
                            onChange={() => setSelectedIntervention(opt.key)}
                            className="accent-slate-800 border-slate-300 w-3.5 h-3.5"
                          />
                          <span className="text-xs font-medium text-slate-700">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="p-2.5 bg-amber-50/60 border border-amber-200/60 rounded-xl flex items-center gap-2.5 select-none">
                    <div className="p-1 bg-amber-100 text-amber-700 rounded-md">
                      <Bell className="w-3.5 h-3.5" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parentAlerted}
                        onChange={(e) => setParentAlerted(e.target.checked)}
                        className="accent-amber-600 rounded border-slate-300 w-3.5 h-3.5"
                      />
                      <span className="text-xs font-bold text-slate-800">Parent alerted administration</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 block">Administrative Notes</label>
                  <textarea
                    rows={4}
                    placeholder="Provide supporting context..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-800 placeholder-slate-400 outline-none resize-none transition h-[calc(100%-1.75rem)]"
                  />
                </div>
              </div>
            </form>

            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2.5 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="h-9 px-4 bg-white font-mono hover:bg-slate-50 text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                form="infraction-form"
                disabled={isSubmitting || !selectedStudentId || selectedViolations.length === 0}
                className={`h-9 px-4 text-white text-xs font-mono font-semibold rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                  infractionData ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>{infractionData ? 'Apply Updates' : 'Save Record'}</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}